import { type CountryListItem, type CountryListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { CountryFormDrawer } from '../components/CountryFormDrawer';
import { listCountries } from '../countries.api';
import {
  useBulkDeleteCountries,
  useBulkSetCountriesStatus,
  useCountriesList,
  useDeleteCountry,
} from '../use-countries';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const CountriesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteCountry = useDeleteCountry();
  const bulkDeleteMut = useBulkDeleteCountries();
  const bulkSetStatusMut = useBulkSetCountriesStatus();

  const columns: ListScreenColumns<CountryListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<CountryListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <span className="font-medium text-zinc-900">
            {row.flagEmoji ? `${row.flagEmoji} ` : ''}
            {row.name}
          </span>
        ),
      },
      {
        id: 'isoCode',
        header: 'ISO',
        sortable: true,
        cell: (row) => (
          <span>
            {row.isoCode}
            {row.isoCode3 ? ` / ${row.isoCode3}` : ''}
          </span>
        ),
      },
      {
        id: 'phoneCode',
        header: 'Phone',
        cell: (row) => <span>{row.phoneCode ?? '-'}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<CountryListItem, CountryListQuery>
      title="Countries"
      description="The curated country list used across the app (addresses, branches, geography pickers)."
      useList={useCountriesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add country' }}
      renderDrawer={({ open, mode, row, close }) => (
        <CountryFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          country={row}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} countr${rows.length === 1 ? 'y' : 'ies'}?`,
            description: 'These countries will be soft-deleted and hidden from lists and pickers.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} countr${rows.length === 1 ? 'y' : 'ies'}?`,
            description: isActive
              ? 'They will appear in geography pickers and lists again.'
              : 'They will be hidden from pickers but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listCountries(q),
        filename: 'countries',
        csv: [
          { header: 'ISO', value: (r) => r.isoCode },
          { header: 'ISO3', value: (r) => r.isoCode3 ?? '' },
          { header: 'Name', value: (r) => r.name },
          { header: 'Phone', value: (r) => r.phoneCode ?? '' },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteCountry.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this country?',
            description: `${row.name} will be soft-deleted and hidden from lists and pickers.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by name or ISO code (min 3 characters)',
        emptyTitle: 'No countries match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load countries',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
